import { normalizeUsernameForLookup } from "./username";
import {
    normalizeShareOrigin,
    resolveConfiguredShareOrigin as resolveShareOrigin,
} from "./shareRouteOrigin";

export const CANONICAL_USER_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export const PROFILE_ROUTE_PREFIX = "/profile";
export const PROFILE_USERNAME_ROUTE_PREFIX = "/@";
export const DEFAULT_PROFILE_SHARE_ORIGIN = "https://muzone.co";

export const normalizeProfileUserId = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const normalized = value.trim().toLowerCase();
    return CANONICAL_USER_ID_REGEX.test(normalized) ? normalized : null;
};

export const buildProfilePath = (userId) => {
    const normalizedUserId = normalizeProfileUserId(userId);
    return normalizedUserId ? `${PROFILE_ROUTE_PREFIX}/${normalizedUserId}` : null;
};

export const normalizeProfileUsername = normalizeUsernameForLookup;

export const buildProfileUsernamePath = (username) => {
    const normalizedUsername = normalizeProfileUsername(username);
    return normalizedUsername ? `${PROFILE_USERNAME_ROUTE_PREFIX}${encodeURIComponent(normalizedUsername)}` : null;
};

export const buildProfileSharePath = (profile) => (
    buildProfileUsernamePath(profile?.username)
    || buildProfilePath(profile?.userId ?? profile?.id)
);

const resolveConfiguredShareOrigin = () => {
    return resolveShareOrigin(
        process.env.REACT_APP_PROFILE_SHARE_BASE_URL,
        DEFAULT_PROFILE_SHARE_ORIGIN,
    );
};

export const buildProfileShareUrl = (profile, origin = resolveConfiguredShareOrigin()) => {
    const profilePath = buildProfileSharePath(profile);
    const normalizedOrigin = normalizeShareOrigin(origin);
    if (!profilePath || !normalizedOrigin) {
        return null;
    }

    return new URL(profilePath, normalizedOrigin).toString();
};

export const isProfileRoutePath = (pathname) => (
    typeof pathname === "string"
    && (
        pathname.startsWith(`${PROFILE_ROUTE_PREFIX}/`)
        || pathname.startsWith(PROFILE_USERNAME_ROUTE_PREFIX)
    )
);

export const getProfileRouteUserId = (pathname) => {
    if (typeof pathname !== "string" || !pathname.startsWith(`${PROFILE_ROUTE_PREFIX}/`)) {
        return null;
    }

    const withoutPrefix = pathname.slice(PROFILE_ROUTE_PREFIX.length + 1);
    const segments = withoutPrefix.split("/").filter(Boolean);
    if (segments.length !== 1) {
        return null;
    }

    return normalizeProfileUserId(segments[0]);
};

export const getProfileRouteUsername = (pathname) => {
    if (typeof pathname !== "string" || !pathname.startsWith(PROFILE_USERNAME_ROUTE_PREFIX)) {
        return null;
    }

    const withoutPrefix = pathname.slice(PROFILE_USERNAME_ROUTE_PREFIX.length);
    const segments = withoutPrefix.split("/").filter(Boolean);
    if (segments.length !== 1) {
        return null;
    }

    try {
        return normalizeProfileUsername(decodeURIComponent(segments[0]));
    } catch (_) {
        return null;
    }
};

export const getProfileRouteLocator = (pathname) => {
    const userId = getProfileRouteUserId(pathname);
    if (userId) {
        return { type: "userId", value: userId };
    }

    const username = getProfileRouteUsername(pathname);
    if (username) {
        return { type: "username", value: username };
    }

    return null;
};
